import {
  toCartItem,
  validateCartChanges,
} from "@customer-app/account/views/OperationAquarius/components/cartValidation";
import { useSmartrrTheme } from "@customer-app/account/views/OperationAquarius/hooks/useSmartrrTheme";
import {
  applyDiscountCode,
  removeDiscountCode,
  removeSubscriptionLineItem,
  updateSubscriptionLineItem,
} from "@customer-app/state/actionCreators/customerPurchaseStates";
import { useToggleModal } from "@customer-app/state/actionCreators/modal";
import {
  VariantPurchaseOptions,
  useTrendingSellingPlanGroup,
  useVariantEnablementForSellingPlanId,
} from "@customer-app/state/reducers/sellingPlanGroups";
import {
  useSmartrrCustomerDispatch,
  useSmartrrCustomerSelector,
} from "@customer-app/state/typedCustomerReduxHooks";
import { getDeliveryMethodType, getShippingAddress } from "@customer-app/utils/getDeliveryMethodTitle";
import { Grid, Link, Stack, Typography, styled } from "@mui/material";
import { prefixClassName } from "@smartrr/shared/components/primitives";
import { AquariusButton } from "@smartrr/shared/customerPortal/components/AquariusButton";
import { IGift } from "@smartrr/shared/entities/Gift";
import { IPurchasable } from "@smartrr/shared/entities/Purchasable";
import { IPurchasableVariant } from "@smartrr/shared/entities/PurchasableVariant";
import { IPurchaseState } from "@smartrr/shared/entities/PurchaseState";
import { IPurchaseStateLineItem } from "@smartrr/shared/entities/PurchaseState/CustomerPurchaseLineItem";
import { ScheduledDeliveryGroupWithPurchSt } from "@smartrr/shared/entities/PurchaseState/scheduleUtils";
import {
  DeliveryMethodType,
  SellingPlanPricingPolicyAdjustmentType,
  SubscriptionDeliveryMethod,
} from "@smartrr/shared/shopifyGraphQL/api";
import { shopifyGidToNumber } from "@smartrr/shared/utils/ensureShopifyGid";
import {
  isRewardsDiscount,
  isValidRewardsDiscount,
  selectPricingPolicyCycleAdjustmentForCycle,
} from "@smartrr/shared/utils/getDiscounts";
import { localize } from "@smartrr/shared/utils/sharedTranslations/localize";
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import { rgba } from "polished";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Trans } from "react-i18next";
import { toast } from "react-toastify";

import { ShowPrice } from "./ShowPrice";
import { useCurrentBundle } from "../../../../../hooks/useCurrentBundle";
import { useMissingVariantChecker } from "../../../utils";
import { ValidViews } from "../../OrderOptionsModalInner";
import { ModalProductList } from "../ModalProductList";
import { Selector } from "../Selectors/Selector";
import { getDeliveryId } from "@customer-app/state/stores/delivery/DeliveryId";
import { LoyaltyStoreAccess } from "../../../../Loyalty/store";
interface IDefaultViewProps {
  delivery: ScheduledDeliveryGroupWithPurchSt<IPurchaseState>;
  openAddonList: () => void;
  gift?: IGift;
  isPrepaid: boolean;
  isSellingPlanDeleted: boolean;

  /** Disabling addons from appearing */
  hideAddons?: boolean;

  setButtonsDisabled?: React.Dispatch<boolean>;

  purchasablesForAdding: IPurchasable[];

  returnToModal?: () => void;

  inManageSubscriptions?: boolean;
}

const DiscountCodeInput = styled("input")`
  background-color: ${props => props.theme.modernCardBackground};
  border: ${props => props.theme.modernButtonBorderWidth}px solid ${props => props.theme.modernLineDividers};
  border-radius: ${props => props.theme.modernButtonBorderRadius}px;
  height: ${props => props.theme.modernButtonBorderHeight}px;
  padding-left: 10px;
  text-transform: uppercase;
  width: 100%;

  ::-webkit-input-placeholder {
    /* WebKit browsers */
    text-transform: none;
  }
  :-moz-placeholder {
    /* Mozilla Firefox 4 to 18 */
    text-transform: none;
  }
  ::-moz-placeholder {
    /* Mozilla Firefox 19+ */
    text-transform: none;
  }
  :-ms-input-placeholder {
    /* Internet Explorer 10+ */
    text-transform: none;
  }
  ::placeholder {
    /* Recent browsers */
    color: ${props => props.theme.modernBodyFontColor};
    opacity: 50%;
    text-transform: none;
  }
`;

export const DefaultView = ({
  delivery,
  openAddonList,
  gift,
  isPrepaid,
  hideAddons,
  isSellingPlanDeleted,
  setButtonsDisabled = () => {
    return;
  },
  returnToModal,
  inManageSubscriptions = false,
  purchasablesForAdding,
}: IDefaultViewProps): JSX.Element => {
  const dispatch = useSmartrrCustomerDispatch();

  const { purchaseState } = delivery;

  const [discountCode, setDiscountCode] = useState("");

  const localizeShorthand = "subscriptions.yourSubscriptions.orderOptionsModal";

  const { sellingPlanId } = purchaseState;

  const skdIdx = delivery.indexFromScheduleStart;

  const { bundle } = useCurrentBundle(delivery);

  const theme = useSmartrrTheme();

  const isSpecialReferralsEnabled = !!LoyaltyStoreAccess.useSpecialReferralsVariantId();

  const [activePurchaseable, setActivePurchaseable] = useState<IPurchasable | null>(null);
  const [activeItem, setActiveItem] = useState<IPurchaseStateLineItem | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const activeQty = useRef<number | null>(null);

  const [selectedVariantOption, setSelectedVariantOption] = useState<VariantPurchaseOptions | undefined>();

  const isPurchaseStatesInitialized = useSmartrrCustomerSelector(state => state.purchaseStates.isInitialized);
  const isSellingPlanGroupsLoading = useSmartrrCustomerSelector(
    state => state.sellingPlanGroups.sellingPlansIsLoading
  );

  const customerEmail = useSmartrrCustomerSelector(state => state.auth.customerRelationship?.email);
  const customerCountryCode = useSmartrrCustomerSelector(
    state => state.auth.customerRelationship?.defaultAddress?.countryCode
  );

  const [showCancelSubscription, setShowCancelSubscription] = useState(false);

  const isVariantMissing = useMissingVariantChecker();

  const trendingSellingPlanGroup = useTrendingSellingPlanGroup();
  const trendingSellingPlanShopifyId = trendingSellingPlanGroup?.sellingPlans[0]
    ? trendingSellingPlanGroup?.sellingPlans[0].shopifyId!
    : undefined;

  // Line items
  const { shippingAddress } = purchaseState;

  const correspondingLineItem = purchaseState.stLineItems.find(
    item => item.pricingPolicy && !item.isAddOn && !item.isRedeemed
  );

  const pricingPolicyAdjustment = correspondingLineItem?.pricingPolicy
    ? selectPricingPolicyCycleAdjustmentForCycle(correspondingLineItem.pricingPolicy, skdIdx)
    : undefined;

  const showAddItemAsSub = theme.modernThemeSettings?.showHide.showAddItemAsSub!;
  const showAddItemAsAddon = theme.modernThemeSettings?.sections.subscriptions.addOns!;

  const showAddItem =
    Boolean(openAddonList) &&
    !delivery.isSkipped &&
    !gift &&
    !isSellingPlanDeleted &&
    !isPrepaid &&
    (hideAddons ? showAddItemAsSub : showAddItemAsAddon) &&
    Boolean(purchasablesForAdding.length);

  const onDiscountCodeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setDiscountCode(e.currentTarget.value);
  }, []);

  const applyDiscountCodeClick = useCallback(async () => {
    setIsLoading(true);

    if (isNil(discountCode) || isEmpty(discountCode)) {
      toast.error(localize(`${localizeShorthand}.missingDiscountCodeErrorToast`));
      setIsLoading(false);
      onCancel();
      return;
    }

    if (isRewardsDiscount(discountCode) && !isValidRewardsDiscount(discountCode, customerEmail!)) {
      toast.error(localize("subscriptions.yourSubscriptions.orderOptionsModal.invalidDiscountCodeErrorToast"));
      return;
    }

    const res = await dispatch(applyDiscountCode(purchaseState.id, discountCode, toast));
    if (res.type === "PURCHASE_STATE_APPLY_DISCOUNT_CODE_SUCCESS") {
      setDiscountCode("");
    }
    setIsLoading(false);
    onCancel();
  }, [customerEmail, purchaseState, discountCode]);

  const onDiscountCodeRemove = useCallback(
    async (discountVendorId: string) => {
      setIsLoading(true);
      await dispatch(removeDiscountCode(purchaseState.id, discountVendorId, toast));
      setIsLoading(false);
      onCancel();
    },
    [purchaseState]
  );

  const onDiscountCodeKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        applyDiscountCodeClick();
      }
    },
    [discountCode]
  );

  const discountCodeAvailable =
    !delivery.isSkipped &&
    purchaseState.purchaseStateStatus !== "PAUSED" &&
    !isSellingPlanDeleted &&
    !isPrepaid &&
    theme.modernThemeSettings?.showHide.showDiscountCodeInput &&
    !isSpecialReferralsEnabled;

  const showAmtSaved =
    !bundle &&
    !isSellingPlanDeleted &&
    !isPrepaid &&
    !!pricingPolicyAdjustment &&
    pricingPolicyAdjustment.adjustmentType === SellingPlanPricingPolicyAdjustmentType.Percentage &&
    pricingPolicyAdjustment.adjustmentValue.percentage > 0;

  const onConfirm = useCallback(
    (vntId: string, variant?: IPurchasableVariant) =>
      activeQty.current ? onVariantSelect(activeItem!, vntId, variant) : onRemove(activeItem?.id!),
    [activeQty, activeItem]
  );

  const onRemove = useCallback(
    async (lineItemId: string) => {
      const isAddon = purchaseState.stLineItems.find(li => li.id === lineItemId)!.isAddOn;
      if (isAddon || purchaseState.stLineItems.length > 1) {
        const isValidUpdate = validateCartChanges(delivery, { removedItemIds: [lineItemId] });
        if (!isValidUpdate) {
          return;
        }
        setIsLoading(true);

        await dispatch(removeSubscriptionLineItem(purchaseState.id, lineItemId))
          .then(res => {
            switch (res.type) {
              case "PURCHASE_STATE_LINE_ITEM_REMOVE_SUCCESS": {
                toast(localize(`${localizeShorthand}.removedItemToast`));
                setShowCancelSubscription(false);
                break;
              }
              case "PURCHASE_STATE_LINE_ITEM_REMOVE_ERROR": {
                toast.error(localize(`${localizeShorthand}.removeItemErrorToast`));
                setShowCancelSubscription(false);
                break;
              }
              case "PURCHASE_STATE_LINE_ITEM_REMOVE_ERROR_LAST_ITEM": {
                /* Do not show toast here */
                setShowCancelSubscription(true);
                break;
              }
            }
          })
          .catch(error => toast(error));
        setIsLoading(false);
        onCancel();
      } else {
        toast.error(localize(`${localizeShorthand}.removeItemQtyErrorToast`));
      }
    },
    [purchaseState, purchaseState.updatedDate, delivery]
  );

  // Changing Qty / variant
  const onVariantSelect = useCallback(
    async (
      activeLineItem: IPurchaseStateLineItem,
      value: string,
      chosenVariant: IPurchasableVariant | undefined
    ) => {
      const variantId = value;
      const quantity = activeQty?.current!;
      const { sellingPlanId } = activeLineItem;
      setIsLoading(true);
      try {
        const duplicatedItems = purchaseState.stLineItems.filter(item => {
          const isUnique = item.id !== activeItem?.id;
          const vntIdMatch = item.vnt.id === variantId;
          const spIdMatch = activeItem?.isAddOn ? true : item.sellingPlanId === sellingPlanId && !item.isAddOn;
          const skdIdxMatch = activeItem?.isAddOn ? item.skdIdx === skdIdx : true;
          const isReferralsLineItem = item.isRedeemed;

          if (vntIdMatch && spIdMatch && skdIdxMatch && isUnique && !isReferralsLineItem) {
            return true;
          }
          return false;
        });

        if (duplicatedItems.length) {
          // remove the duplicate item and move quantity from it into the updated item
          const joinedQuantity = quantity + duplicatedItems[0].quantity;
          const isValid = validateCartChanges(delivery, {
            removedItemIds: [duplicatedItems[0].id],
            updatedItems: [
              {
                ...toCartItem(delivery, activeItem!),
                quantity: joinedQuantity,
              },
            ],
          });
          if (!isValid) {
            return;
          }

          await dispatch(removeSubscriptionLineItem(purchaseState.id, duplicatedItems[0].id));
          await dispatch(
            updateSubscriptionLineItem(
              purchaseState.id,
              activeItem?.id!,
              {
                variantId,
                quantity: joinedQuantity,
                countryCode: customerCountryCode,
              },
              toast
            )
          );
        } else {
          const isValid = validateCartChanges(delivery, {
            updatedItems: [
              {
                ...toCartItem(delivery, activeItem!, chosenVariant),
                variantShopifyId: chosenVariant ? chosenVariant.shopifyId : activeItem!.vnt.shopifyId,
                sellingPlanShopifyId: sellingPlanId,
                quantity: activeQty.current!,
              },
            ],
          });
          if (!isValid) {
            return;
          }
          await dispatch(
            updateSubscriptionLineItem(
              purchaseState.id,
              activeItem?.id!,
              {
                variantId,
                quantity,
                countryCode: customerCountryCode,
              },
              toast
            )
          );
        }
      } finally {
        setIsLoading(false);
        onCancel();
      }
    },
    [purchaseState, activeItem, activeQty?.current, delivery, toast]
  );

  const onCancel = useCallback(() => {
    setActiveItem(null);
    setActivePurchaseable(null);
    setSelectedVariantOption(undefined);
    setButtonsDisabled(false);
  }, [purchaseState.updatedDate]);

  const enabledVariantOptions = useVariantEnablementForSellingPlanId(
    activePurchaseable!,
    activeItem?.sellingPlanId ?? sellingPlanId,
    true
  );

  const toggleModal = useToggleModal();

  const goToManageSubscriptions = useCallback(() => {
    const selectedView: ValidViews = inManageSubscriptions ? "Cancel" : "Default";

    toggleModal("MANAGE_SUBSCRIPTIONS", {
      deliveryId: getDeliveryId(delivery),
      purchasablesForAdding: activePurchaseable ? [activePurchaseable] : [],
      initialView: selectedView,
      generatePurchasables: false,
    });
  }, [delivery, purchaseState, activePurchaseable]);

  const getSubscriptionShipping = (gift?: IGift, deliveryMethod?: SubscriptionDeliveryMethod) => {
    const deliveryMethodType = getDeliveryMethodType(deliveryMethod);
    const address = getShippingAddress(shippingAddress, deliveryMethod, gift);
    let shipTo = "";

    switch (deliveryMethodType) {
      case DeliveryMethodType.Local: {
        shipTo = localize("delivery.local");
        break;
      }
      case DeliveryMethodType.PickUp: {
        shipTo = localize("delivery.pickup");
        break;
      }
      default: {
        shipTo = localize("delivery.shipTo");
        break;
      }
    }

    if (gift) {
      shipTo = localize(`${localizeShorthand}.giftedTo`);
    }

    return (
      <React.Fragment>
        <Typography variant="body2" style={{ whiteSpace: "nowrap" }}>
          {shipTo}
        </Typography>
        <Typography variant="body1" style={{ textAlign: "end" }}>
          {address}
        </Typography>
      </React.Fragment>
    );
  };
  const disableActivePurchaseable = () => {
    const { sellingPlanId } = purchaseState;
    const maybeVariantId = activeItem?.vnt.shopifyId;
    const variantId = String(maybeVariantId ? shopifyGidToNumber(maybeVariantId) : "");
    const isAddOn = activeItem?.isAddOn ?? false;
    const missingVariant = isVariantMissing(variantId, sellingPlanId ?? "");

    return !missingVariant || isAddOn;
  };

  useEffect(() => {
    if (activePurchaseable && activeItem && activeItem.vnt) {
      const activeVnt = enabledVariantOptions.find(option => option.vnt.id === activeItem.vnt.id);
      setSelectedVariantOption(activeVnt);
    }
  }, [activePurchaseable?.id, activeItem?.vnt]);

  return (
    <React.Fragment>
      <ModalProductList
        delivery={delivery}
        activePurchaseable={activePurchaseable}
        activeItem={activeItem}
        setActivePurchaseable={activePurchaseable => {
          setButtonsDisabled(true);
          setActivePurchaseable(activePurchaseable);
        }}
        setActiveItem={setActiveItem}
        activeQty={activeQty}
        disabled={isLoading}
        variantIdFromDropdown={selectedVariantOption?.vnt?.id}
        enabledVariantOptions={enabledVariantOptions}
        onRemove={onRemove}
        isPrepaid={isPrepaid}
        isSellingPlanDeleted={isSellingPlanDeleted}
        hideAddons={hideAddons}
        returnToModal={returnToModal}
        selectOnClick
        showProductName
        showVariant
      />

      {activePurchaseable && activeItem && disableActivePurchaseable() ? (
        <Selector
          purchasable={activePurchaseable}
          currency={purchaseState.currency}
          isLoading={isLoading}
          setSelectedVariantOption={setSelectedVariantOption}
          selectedVariantOption={selectedVariantOption}
          sellingPlanId={activeItem.sellingPlanId}
          options={{
            includeAddons: activeItem.isAddOn,
            includeTrending: trendingSellingPlanShopifyId
              ? trendingSellingPlanShopifyId === activeItem.sellingPlanId
              : false, // This is the equivalent of having `activeItem.isTrending` if it existed
          }}
          onSubmitHandler={() => {
            const variantId = selectedVariantOption?.vnt?.id || activeItem.vnt?.id;
            const variant = selectedVariantOption?.vnt || activeItem.vnt;
            onConfirm(variantId, variant);
          }}
          onCancelHandler={onCancel}
        />
      ) : null}

      {isLoading ? (
        <React.Fragment />
      ) : (
        <React.Fragment>
          {showCancelSubscription ? (
            <Stack
              sx={{
                borderTop: `1px solid ${theme.modernLineDividers}`,
                borderBottom: `1px solid ${theme.modernLineDividers}`,
                padding: "15px 0",
                textAlign: "center",
              }}
              className={prefixClassName("last-item-removed")}
            >
              <Typography
                variant="caption1"
                style={{
                  opacity: "70%",
                  width: "100%",
                }}
              >
                <Trans
                  i18nKey={`${localizeShorthand}.cancelSubscription.plainText`}
                  values={{ linkText: localize(`${localizeShorthand}.cancelSubscription.linkText`) }}
                  components={[
                    <React.Fragment key={0} />,
                    <Link
                      component="span"
                      key={1}
                      underline="always"
                      sx={{
                        color: theme.modernBodyFontColor,
                        cursor: "pointer !important",
                        textDecorationColor: rgba(theme.modernBodyFontColor!, 0.4),
                      }}
                      onClick={() => goToManageSubscriptions()}
                    />,
                  ]}
                />
              </Typography>
            </Stack>
          ) : null}
          {showAddItem ? (
            <Stack
              sx={{
                borderTop: `1px solid ${theme.modernLineDividers}`,
                borderBottom: `1px solid ${theme.modernLineDividers}`,
                padding: "15px 0",
              }}
              justifyContent="space-between"
              direction="row"
              className={prefixClassName("ship-to")}
            >
              <Typography
                variant="body1"
                className={prefixClassName("add-item")}
                id={`add-item-as-${hideAddons ? "subscription-item" : "addon"}`}
                onClick={openAddonList}
                sx={{
                  "&:hover": {
                    cursor: "pointer",
                  },
                  textDecoration: "underline",
                }}
              >
                + {localize(`general.addItemBtn`)}
              </Typography>
            </Stack>
          ) : null}
          <Stack
            sx={{
              borderBottom: `1px solid ${theme.modernLineDividers}`,
              padding: "15px 0",
            }}
            justifyContent="space-between"
            direction="row"
            className={prefixClassName("ship-to")}
            data-testid="ship-to-row"
          >
            {getSubscriptionShipping(gift, purchaseState.deliveryMethod)}
          </Stack>
          <Stack
            spacing={"10px"}
            sx={{ marginTop: "10px" }}
            className={prefixClassName("discounts-wrapper", "pricing-row")}
            data-testid="pricing-row"
          >
            <ShowPrice
              delivery={delivery}
              includeAddons={!inManageSubscriptions}
              showAmountSaved={showAmtSaved}
              onDiscountCodeRemove={onDiscountCodeRemove}
            />
            {discountCodeAvailable ? (
              <Grid
                container
                direction="row"
                spacing={"10px"}
                className={prefixClassName("discount-input-wrapper")}
              >
                <Grid item md={10} xs={9} sx={{ padding: "0px !important" }}>
                  <DiscountCodeInput
                    placeholder={localize(`${localizeShorthand}.discountInput`)}
                    className={prefixClassName("edit-subscription-apply-discount")}
                    onChange={onDiscountCodeChange}
                    onKeyDown={onDiscountCodeKeyDown}
                    value={discountCode}
                  />
                </Grid>
                <Grid item md={2} xs={3} sx={{ paddingTop: "0px !important" }}>
                  <AquariusButton sx={{ marginTop: "0px" }} onClick={applyDiscountCodeClick}>
                    {localize(`${localizeShorthand}.applyBtn`)}
                  </AquariusButton>
                </Grid>
              </Grid>
            ) : null}
          </Stack>
        </React.Fragment>
      )}
    </React.Fragment>
  );
};
